import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Textarea, Select } from '../../components/ui/Input'
import { EmptyState } from '../../components/ui/EmptyState'
import { useAuth } from '../../auth/AuthContext'
import { MessageBubble } from './MessageBubble'
import { useChatSession } from './useChatSession'
import { useAutoScrollToBottom } from './useAutoScrollToBottom'
import type { ChatMessage } from '../../api/types'
import styles from './FloatingChatWidget.module.css'

/** Schwebendes Chat-Fenster unten rechts, auf jeder Seite außer /chat selbst (dort läuft
 * bereits die volle Teamchat-Ansicht) — nutzt denselben useChatSession-Hook wie ChatPage,
 * daher identisches Verhalten (Join/Read/Realtime) ohne doppelte Socket-Verkabelung. */
export function FloatingChatWidget() {
  const { me, hasPermission } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null)

  const {
    channelsQuery,
    unreadQuery,
    selectedChannelId,
    setSelectedChannelId,
    messages,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    notifyTyping,
    typingLabel,
  } = useChatSession()
  const messagesRef = useRef<HTMLDivElement>(null)
  useAutoScrollToBottom(messagesRef, selectedChannelId, messages)

  if (!me) return null

  const totalUnread = Object.values(unreadQuery.data ?? {}).reduce((sum, n) => sum + n, 0)

  const handleSend = () => {
    if (!draft.trim() || !selectedChannelId) return
    sendMessage(draft, replyTarget?.id)
    setDraft('')
    setReplyTarget(null)
  }

  return (
    <div className={styles.wrap}>
      {open ? (
        <div className={styles.panel}>
          <div className={styles.header}>
            {channelsQuery.data && channelsQuery.data.length > 1 ? (
              <Select
                value={selectedChannelId ?? ''}
                onChange={(e) => setSelectedChannelId(e.target.value)}
                className={styles.channelSelect}
              >
                {channelsQuery.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    # {c.name}
                    {unreadQuery.data?.[c.id] ? ` (${unreadQuery.data[c.id]})` : ''}
                  </option>
                ))}
              </Select>
            ) : (
              <span className={styles.headerTitle}># {channelsQuery.data?.[0]?.name ?? 'Teamchat'}</span>
            )}
            <div className={styles.headerActions}>
              <button className={styles.iconBtn} title="Vollansicht öffnen" onClick={() => navigate('/chat')}>
                ⤢
              </button>
              <button className={styles.iconBtn} title="Schließen" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
          </div>

          <div className={styles.messages} ref={messagesRef}>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserId={me.user.id}
                canModerate={hasPermission('chat.manage_channels')}
                onReply={setReplyTarget}
                onEdit={editMessage}
                onDelete={deleteMessage}
                onToggleReaction={(msg, emoji) => toggleReaction(msg, emoji, me.user.id)}
              />
            ))}
            {messages.length === 0 ? <EmptyState mascot title="Noch keine Nachrichten" /> : null}
          </div>

          <div className={styles.inputBar}>
            {typingLabel ? <div className={styles.typingIndicator}>{typingLabel}</div> : null}
            {replyTarget ? (
              <div className={styles.replyBar}>
                <span>
                  Antwort an {replyTarget.author.displayName}: {replyTarget.body.slice(0, 40)}
                </span>
                <button onClick={() => setReplyTarget(null)} className={styles.iconBtn}>
                  ✕
                </button>
              </div>
            ) : null}
            <div className={styles.inputRow}>
              <Textarea
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value)
                  notifyTyping()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Nachricht schreiben…"
                className={styles.textarea}
              />
              <Button variant="primary" size="sm" onClick={handleSend} disabled={!draft.trim()}>
                Senden
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <button className={styles.fab} onClick={() => setOpen((v) => !v)} aria-label={open ? 'Chat schließen' : 'Chat öffnen'}>
        {open ? '✕' : '💬'}
        {!open && totalUnread > 0 ? <span className={styles.fabBadge}>{totalUnread > 9 ? '9+' : totalUnread}</span> : null}
      </button>
    </div>
  )
}
